import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {AiAttributeServiceContext, AppContextContext, VulSourceServiceContext, VulViewServiceContext} from "../../Contexts";
import {DatePickerEditor, FilterItem, FilterItemProps, InputEditor, SelectEditor} from "../tablefilter/TableFilter";
import {SearchFilter} from "../tablefilter/SearchFilter";
import QueryOptions from "../../sal-ui/QueryOptions";
import VulSource, {vulSourceSystemLastComparator} from "../../domain/VulSource";
import VulView, {isManagedVulView, vulViewSystemLastComparator} from "../../domain/VulView";
import type {RefSelectProps} from "antd";
import {Form, Input, InputNumber, Select, Tag, Tooltip} from "antd";
import * as styles from "./VulSourceItemFilter.module.css";
import * as tackStyles from "./Tack.module.css";
import * as descriptionStyles from "../SelectItemDescription.module.css";
import AttributeDefinition from "../../domain/AttributeDefinition";
import AiAttribute, {AI_ATTRIBUTE_RATING_OPERATORS, AI_ATTRIBUTE_SUMMARY_OPERATORS, AI_ATTRIBUTE_TEXT_OPERATORS} from "../../domain/AiAttribute";
import {AttributeValueType} from "../../domain/AttributeValueType";
import {QuestionCircleTwoTone} from "@ant-design/icons";
import MarkdownContent from "../common/MarkdownContent";
import {Feature} from "../../domain/Feature";
import _ from "lodash";
import dayjs from "dayjs";
import {isValuelessAttributeOperator} from "../../utils/RqbOperatorUtils";

export const MIN_ATTRIBUTE_VALUE_SEARCH_LENGTH = 3;

const DATE_FILTER_PRESETS = [
    {label: "Today", value: () => dayjs().startOf("day")},
    {label: "Yesterday", value: () => dayjs().subtract(1, "day").startOf("day")},
    {label: "1 week ago", value: () => dayjs().subtract(1, "week").startOf("day")},
    {label: "1 month ago", value: () => dayjs().subtract(1, "month").startOf("day")},
    {label: "3 months ago", value: () => dayjs().subtract(3, "month").startOf("day")},
    {label: "6 months ago", value: () => dayjs().subtract(6, "month").startOf("day")},
    {label: "1 year ago", value: () => dayjs().subtract(1, "year").startOf("day")},
];

const ATTRIBUTE_NUMERIC_OPERATORS = [
    {value: "jsonGe", label: "≥"},
    {value: "jsonEq", label: "="},
    {value: "jsonLe", label: "≤"},
    {value: "jsonPresent", label: "is present"},
    {value: "jsonMissing", label: "is missing"},
];

const ATTRIBUTE_BOOLEAN_OPERATORS = [
    {value: "jsonEq", label: "="},
    {value: "jsonPresent", label: "is present"},
    {value: "jsonMissing", label: "is missing"},
];

const ATTRIBUTE_TEXT_OPERATORS = [
    {value: "jsonEq", label: "="},
    {value: "jsonIcontains", label: "contains"},
    {value: "not(jsonIcontains)", label: "does not contain"},
    {value: "jsonRegex", label: "matches regex"},
    {value: "not(jsonRegex)", label: "does not match regex"},
    {value: "jsonPresent", label: "is present"},
    {value: "jsonMissing", label: "is missing"},
];

const ATTRIBUTE_BOOLEAN_VALUE_OPTIONS = [
    {value: true, label: "true"},
    {value: false, label: "false"},
];

const ATTRIBUTE_NAME_POPUP_STYLES = {
    root: {
        width: "max-content",
        minWidth: 260,
        maxWidth: "min(520px, calc(100vw - 32px))"
    }
};

interface VulSourceItemProps extends FilterItemProps {
    sources: VulSource[];
    loading: boolean;
}

function VulSourceItem(props: VulSourceItemProps) {
    const sources = useMemo(
        () => [...(props.sources ?? [])].sort(vulSourceSystemLastComparator),
        [props.sources]
    );

    if (props.loading && props.value?.length > 0) {
        return <LoadingFilterValue/>;
    }

    return (
        <SelectEditor minWidth={120}
                      multiple={true}
                      filterOption={optionLabelContains}
                      value={props.value}
                      optionLabelProp="label"
                      onChange={value => props.onChange(value)}>

            {sources?.map(source =>
                <Select.Option key={source.id} value={source.id} label={source.name}>
                    {source.owner === 'SYSTEM' && <div className={`${tackStyles.tack} ${tackStyles['tack-seal']}`}><span className={tackStyles['tack-text']} color={"#440084"}>SEAL</span></div>}
                    {source.owner === 'ORGANIZATION' && <div className={`${tackStyles.tack} ${tackStyles['tack-org']}`}><span className={tackStyles['tack-text']} color={"#87d068"}>ORG</span></div>}

                    <div className={tackStyles.item}>
                        <div className={descriptionStyles['name-container']}>
                            <span className={descriptionStyles.name}>{source.name}</span>
                            {source.description && <Tooltip title={<MarkdownContent children={source.description}/>}><QuestionCircleTwoTone/></Tooltip>}
                        </div>
                        <div className={descriptionStyles.description}>{source.description}</div>
                    </div>
                </Select.Option>
            )}

        </SelectEditor>
    )
}

interface VulViewItemProps extends FilterItemProps {
    vulViews: VulView[];
    loading: boolean;
}

function VulViewItem(props: VulViewItemProps) {
    const views = useMemo(
        () => [...(props.vulViews ?? [])].sort(vulViewSystemLastComparator),
        [props.vulViews]
    );
    const systemViews = useMemo(
        () => views.filter(vulView => vulView.owner === 'SYSTEM'),
        [views]
    );
    const orgViewGroups = useMemo(() => {
        return views
            .filter(vulView => vulView.owner === 'ORGANIZATION')
            .reduce<Record<string, VulView[]>>((groups, vulView) => {
                const organizationName = vulView.organization?.name ?? "—";

                (groups[organizationName] ??= []).push(vulView);

                return groups;
            }, {});
    }, [views]);

    if (props.loading && props.value?.length > 0) {
        return <LoadingFilterValue/>;
    }

    const renderViewOption = (vulView: VulView) => (
        <Select.Option key={vulView.id} value={vulView.id} label={vulView.name}>
            <div>
                <div className={descriptionStyles['name-container']}>
                    <span className={descriptionStyles.name}>{vulView.name}</span>
                    {isManagedVulView(vulView) && (
                        <Tag color="blue" variant={"filled"} title="This view is managed automatically through Interests.">
                            managed
                        </Tag>
                    )}
                    {vulView.description && <Tooltip title={<MarkdownContent children={vulView.description}/>}><QuestionCircleTwoTone/></Tooltip>}
                </div>
                <div className={descriptionStyles.description}>{vulView.description}</div>
            </div>
        </Select.Option>
    );

    return (
        <SelectEditor
            minWidth={130}
            showSearch
            multiple={true}
            filterOption={optionLabelContains}
            value={props.value}
            optionLabelProp="label"
            onChange={value => props.onChange(value)}
        >
            {Object.entries(orgViewGroups).map(([organizationName, vulViews]) => (
                <Select.OptGroup key={organizationName} label={organizationName}>
                    {vulViews.map(renderViewOption)}
                </Select.OptGroup>
            ))}

            {systemViews.length > 0 && (
                <Select.OptGroup label="System">
                    {systemViews.map(renderViewOption)}
                </Select.OptGroup>
            )}
        </SelectEditor>
    )
}

function LoadingFilterValue() {
    return <span className={styles['loading-filter-value']}>Loading...</span>;
}

function normalizeSearchText(value?: string) {
    return value?.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "") ?? "";
}

function optionLabelContains(inputValue: string, option?: {label?: unknown}) {
    const normalizedInput = normalizeSearchText(inputValue);
    const normalizedLabel = normalizeSearchText(String(option?.label ?? ""));

    return normalizedLabel.includes(normalizedInput);
}

function TimestampItem(props: FilterItemProps) {
    return (
        <DatePickerEditor
            placeholder={"Timestamp"}
            presets={DATE_FILTER_PRESETS}
            value={props.value}
            onChange={value => props.onChange(value)}
        />
    )
}

function AttributeValueItem(props: FilterItemProps) {
    const trimmedValue = String(props.value ?? "").trim();
    const isTooShort = trimmedValue.length > 0 && trimmedValue.length < MIN_ATTRIBUTE_VALUE_SEARCH_LENGTH;
    const errorMessage = `Enter at least ${MIN_ATTRIBUTE_VALUE_SEARCH_LENGTH} characters.`;
    const inputEditor = (
        <InputEditor
            placeholder={`At least ${MIN_ATTRIBUTE_VALUE_SEARCH_LENGTH} characters`}
            value={props.value}
            status={isTooShort ? "error" : undefined}
            onChange={value => props.onChange(value)}
        />
    );

    if (!isTooShort) {
        return inputEditor;
    }

    return (
        <Form.Item
            className={styles['attribute-value-form-item']}
            validateStatus="error"
            help={errorMessage}
        >
            {inputEditor}
        </Form.Item>
    )
}

function CveIdItem(props: FilterItemProps) {
    return (
        <InputEditor
            value={props.value}
            onChange={value => props.onChange(value)}
        />
    )
}

interface AttributeItemProps extends FilterItemProps {
    attributeDefinitions?: AttributeDefinition[];
}

type FocusableInputRef = {
    focus?: () => void;
};

function getAttributeOperators(valueType?: AttributeValueType) {
    switch (valueType) {
        case AttributeValueType.NUMBER:
            return ATTRIBUTE_NUMERIC_OPERATORS;
        case AttributeValueType.BOOLEAN:
            return ATTRIBUTE_BOOLEAN_OPERATORS;
        default:
            return ATTRIBUTE_TEXT_OPERATORS;
    }
}

function formatAttributeValue(value: unknown, valueType?: AttributeValueType) {
    if (valueType !== AttributeValueType.NUMBER) {
        return value;
    }

    return typeof value === "number" ? value : parseFloat(value as string);
}

function AttributeItem(props: AttributeItemProps) {
    const booleanValueSelectRef = useRef<RefSelectProps>(null);
    const numberValueInputRef = useRef<FocusableInputRef>(null);
    const textValueInputRef = useRef<FocusableInputRef>(null);
    const [attributeName, setAttributeName] = useState<string>();
    const [operator, setOperator] = useState<string>();
    const [attributeValue, setAttributeValue] = useState<unknown>();

    useEffect(() => {
        if (props.value === undefined || props.attributeDefinitions === undefined) {
            return;
        }

        setAttributeName(props.value.name);
        setOperator(props.value.operator);
        setAttributeValue(props.value.value);
    }, [props.value, props.attributeDefinitions]);

    const definition = props.attributeDefinitions?.find(item => item.name === attributeName);
    const operators = getAttributeOperators(definition?.valueType);

    useEffect(() => {
        if (operator && !operators.some(item => item.value === operator)) {
            setOperator(undefined);
        }
    }, [operator, operators]);

    useEffect(() => {
        if (attributeName !== undefined && isValuelessAttributeOperator(operator)) {
            props.onChange({
                name: attributeName,
                value: undefined,
                operator
            });
        } else if (attributeName !== undefined && operator !== undefined && attributeValue !== undefined) {
            props.onChange({
                name: attributeName,
                value: formatAttributeValue(attributeValue, definition?.valueType),
                operator
            });
        }
    }, [attributeName, operator, attributeValue]);

    return (
        <div className={styles['attribute-editor']}>
            <Select
                className={styles['attribute-name']}
                size={"small"}
                value={attributeName}
                popupMatchSelectWidth={false}
                styles={{popup: ATTRIBUTE_NAME_POPUP_STYLES}}
                showSearch
                optionFilterProp="label"
                onChange={onAttributeNameChange}
                options={props.attributeDefinitions?.map(definition => ({
                    value: definition.name,
                    label: definition.name
                }))}
            />

            <Select
                size={"small"}
                variant={"borderless"}
                value={operator}
                popupMatchSelectWidth={false}
                onChange={onOperatorChange}
                options={operators}
            />

            {definition?.valueType === AttributeValueType.NUMBER && !isValuelessAttributeOperator(operator) && <InputNumber
                ref={numberValueInputRef}
                className={styles['attribute-value']}
                size={"small"}
                value={attributeValue}
                onChange={value => setAttributeValue(value)}
            />}

            {definition?.valueType === AttributeValueType.BOOLEAN && !isValuelessAttributeOperator(operator) && <Select
                ref={booleanValueSelectRef}
                className={styles['attribute-value']}
                size={"small"}
                value={attributeValue}
                popupMatchSelectWidth={false}
                onChange={value => setAttributeValue(value)}
                options={ATTRIBUTE_BOOLEAN_VALUE_OPTIONS}
            />}

            {definition?.valueType !== AttributeValueType.NUMBER && definition?.valueType !== AttributeValueType.BOOLEAN && !isValuelessAttributeOperator(operator) && <Input
                ref={textValueInputRef}
                className={styles['attribute-value']}
                size={"small"}
                value={attributeValue}
                onChange={(e: any) => setAttributeValue(e.target.value)}
            />}

        </div>
    )

    function onAttributeNameChange(value: string) {
        const nextDefinition = props.attributeDefinitions?.find(item => item.name === value);
        const nextOperator = getAttributeOperators(nextDefinition?.valueType)[0]?.value;

        setAttributeName(value);
        setOperator(nextOperator);

        if (nextDefinition?.valueType === AttributeValueType.BOOLEAN) {
            setAttributeValue(true);
            focusAttributeValueInput(nextDefinition.valueType);

            return;
        }

        setAttributeValue(undefined);
        props.onChange({
            name: value,
            value: undefined,
            operator: nextOperator
        });

        focusAttributeValueInput(nextDefinition?.valueType);
    }

    function onOperatorChange(value: string) {
        setOperator(value);

        if (isValuelessAttributeOperator(value)) {
            setAttributeValue(undefined);
        } else if (definition?.valueType === AttributeValueType.BOOLEAN && attributeValue === undefined) {
            setAttributeValue(true);
        } else if (attributeValue === undefined) {
            props.onChange({
                name: attributeName,
                value: undefined,
                operator: value
            });
        }
    }

    function focusAttributeValueInput(valueType?: AttributeValueType) {
        requestAnimationFrame(() => {
            if (valueType === AttributeValueType.NUMBER) {
                numberValueInputRef.current?.focus?.();
            } else if (valueType === AttributeValueType.BOOLEAN) {
                booleanValueSelectRef.current?.focus();
            } else {
                textValueInputRef.current?.focus?.();
            }
        });
    }
}

function getOperatorsForAiAttribute(resultType?: string) {
    switch (resultType) {
        case "VALUE_RATING":
            return {operators: AI_ATTRIBUTE_RATING_OPERATORS, defaultOperator: "ge"};
        case "VALUE_SUMMARY":
            return {operators: AI_ATTRIBUTE_SUMMARY_OPERATORS, defaultOperator: "textContains"};
        default:
            return {operators: AI_ATTRIBUTE_TEXT_OPERATORS, defaultOperator: "textEq"};
    }
}

interface AiAttributeFilterValue {
    aiAttributeId?: string;
    operator?: string;
    value?: any;
}

function AiAttributeFilterItem(props: FilterItemProps) {
    const appContext = useContext(AppContextContext);
    const aiAttributeService = useContext(AiAttributeServiceContext);

    const [allAiAttributes, setAllAiAttributes] = useState<AiAttribute[]>([]);
    const [allAiAttributesLoading, setAllAiAttributesLoading] = useState(true);
    const [aiAttributeId, setAiAttributeId] = useState<string>();
    const [operator, setOperator] = useState<string>();
    const [value, setValue] = useState<any>();
    const lastEmittedValueRef = useRef<any>();

    useEffect(() => {
        aiAttributeService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setAllAiAttributes(result.data))
            .finally(() => setAllAiAttributesLoading(false));
    }, [aiAttributeService]);

    const selectedAiAttribute = allAiAttributes.find(c => c.id === aiAttributeId);
    const canSeeSystemAiAttributes = appContext.user.hasFeature(Feature.AI);

    const systemAiAttributes = useMemo(
        () => canSeeSystemAiAttributes ? allAiAttributes.filter(c => c.owner === "SYSTEM") : [],
        [allAiAttributes, canSeeSystemAiAttributes]
    );
    const orgAiAttributeGroups = useMemo(() => {
        return allAiAttributes.filter(c => c.owner === "ORGANIZATION")
            .reduce<Record<string, AiAttribute[]>>((groups, c) => {
                const orgName = c.organization?.name ?? "—";

                (groups[orgName] ??= []).push(c);

                return groups;
            }, {});
    }, [allAiAttributes]);

    const {operators} = getOperatorsForAiAttribute(selectedAiAttribute?.resultType);

    useEffect(() => {
        if (_.isEqual(props.value, lastEmittedValueRef.current)) {
            return;
        }

        lastEmittedValueRef.current = props.value;

        if (props.value === undefined) {
            setAiAttributeId(undefined);
            setOperator(undefined);
            setValue(undefined);

            return;
        }

        setAiAttributeId(props.value.aiAttributeId);
        setOperator(props.value.operator);
        setValue(props.value.value);
    }, [props.value]);

    const isRating = selectedAiAttribute?.resultType === "VALUE_RATING";
    const allowedValues = selectedAiAttribute?.allowedValues;

    if (allAiAttributesLoading && props.value?.aiAttributeId) {
        return <LoadingFilterValue/>;
    }

    return (
        <div className={styles['attribute-editor']}>
            <Select
                size={"small"}
                placeholder={"AI attribute"}
                value={aiAttributeId}
                loading={allAiAttributesLoading}
                disabled={allAiAttributesLoading}
                popupMatchSelectWidth={false}
                onChange={onAiAttributeChange}
                style={!aiAttributeId ? {minWidth: 160} : undefined}
                showSearch
                filterOption={optionLabelContains}
                optionLabelProp="label"
            >
                {Object.entries(orgAiAttributeGroups).map(([orgName, aiAttributes]) => (
                    <Select.OptGroup key={orgName} label={orgName}>
                        {aiAttributes.map(c => (
                            <Select.Option key={c.id} value={c.id} label={c.name}>{c.name}</Select.Option>
                        ))}
                    </Select.OptGroup>
                ))}
                {systemAiAttributes.length > 0 && (
                    <Select.OptGroup label="System">
                        {systemAiAttributes.map(c => (
                            <Select.Option key={c.id} value={c.id} label={c.name}>{c.name}</Select.Option>
                        ))}
                    </Select.OptGroup>
                )}
            </Select>

            <Select
                size={"small"}
                variant={"borderless"}
                value={operator}
                popupMatchSelectWidth={false}
                onChange={v => updateDraft({aiAttributeId, operator: v, value})}
                options={operators}
            />

            {isRating ? (
                <InputNumber
                    className={styles['attribute-value']}
                    size={"small"}
                    min={0}
                    max={1}
                    step={0.05}
                    value={value}
                    onChange={(v) => updateDraft({aiAttributeId, operator, value: v ?? undefined})}
                />
            ) : allowedValues?.length > 0 ? (
                <Select
                    className={styles['attribute-value']}
                    size={"small"}
                    value={value}
                    onChange={(v) => updateDraft({aiAttributeId, operator, value: v})}
                    options={allowedValues.map(v => ({label: v, value: v}))}
                    showSearch
                />
            ) : (
                <Input
                    className={styles['attribute-value']}
                    size={"small"}
                    value={value}
                    onChange={(e: any) => updateDraft({aiAttributeId, operator, value: e.target.value})}
                />
            )}
        </div>
    );

    function onAiAttributeChange(nextAiAttributeId: string) {
        const nextAiAttribute = allAiAttributes.find(item => item.id === nextAiAttributeId);
        const nextOperators = getOperatorsForAiAttribute(nextAiAttribute?.resultType);
        const nextOperator = nextOperators.operators.some(item => item.value === operator)
            ? operator
            : nextOperators.defaultOperator;

        updateDraft({
            aiAttributeId: nextAiAttributeId,
            operator: nextOperator,
            value: undefined
        });
    }

    function updateDraft(nextValue: AiAttributeFilterValue) {
        setAiAttributeId(nextValue.aiAttributeId);
        setOperator(nextValue.operator);
        setValue(nextValue.value);

        if (!isCompleteAiAttributeFilter(nextValue)) {
            if (props.value !== undefined) {
                lastEmittedValueRef.current = undefined;
                props.onChange(undefined);
            }

            return;
        }

        lastEmittedValueRef.current = nextValue;
        props.onChange(nextValue);
    }

    function isCompleteAiAttributeFilter(value: AiAttributeFilterValue) {
        return !!value.aiAttributeId
            && !!value.operator
            && value.value !== undefined
            && value.value !== null
            && value.value !== '';
    }
}

interface VulSourceItemFilterProps {
    className?: string;
    values?: any;
    onChange: (values: any) => void;
    showViews?: boolean;
    showAiAttributeFilters?: boolean;
    immediateMode?: boolean;
    attributeDefinitions?: AttributeDefinition[];
    persistentStateIdent?: string;
}

export function createDefaultVulSourceItemFilterValues() {
    return {
        attributeValue: '',
        createdFrom: dayjs().subtract(3, "month").startOf("day"),
        cveId: '',
        vulView: [],
        sources: [],
        aiAttributeFilter: []
    };
}

function VulSourceItemFilter(props: VulSourceItemFilterProps) {
    const vulViewService = useContext(VulViewServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const showViews = props.showViews ?? true;
    const showAiAttributeFilters = props.showAiAttributeFilters ?? true;
    const [vulViews, setVulViews] = useState<VulView[]>([]);
    const [sources, setSources] = useState<VulSource[]>([]);
    const [vulViewsLoading, setVulViewsLoading] = useState(showViews);
    const [sourcesLoading, setSourcesLoading] = useState(true);

    useEffect(() => {
        if (showViews) {
            vulViewService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
                .then(value => setVulViews(value.data))
                .finally(() => setVulViewsLoading(false));
        }

        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(value => setSources(value.data))
            .finally(() => setSourcesLoading(false));

    }, []);

    const vulViewItem = {
        name: "vulView",
        key: "vulView",
        label: "View",
        render: (props) => <VulViewItem {...props} vulViews={vulViews} loading={vulViewsLoading}/>
    };

    const items: FilterItem[] = [];

    if (showViews) {
        items.push(vulViewItem);
    }

    items.push(
        {
            name: "sources",
            key: "sources",
            label: "Sources",
            render: (props) => <VulSourceItem {...props} sources={sources} loading={sourcesLoading}/>
        },
        {
            name: "cveId",
            key: "cveId",
            label: "CVE ID",
            render: (props) => <CveIdItem {...props} />
        },
        {
            name: "attributeValue",
            key: "attributeValue",
            label: "Attribute value",
            render: (props) => <AttributeValueItem {...props} />
        },
        {
            name: "attributes",
            key: "attributes",
            label: "Attribute",
            multiple: true,
            render: (itemProps) => <AttributeItem attributeDefinitions={props.attributeDefinitions} {...itemProps} />
        },
        {
            name: "createdFrom",
            key: "createdFrom",
            label: "Created from",
            render: (props) => <TimestampItem {...props} />
        },
        {
            name: "createdTo",
            key: "createdTo",
            label: "Created to",
            render: (props) => <TimestampItem {...props} />
        },
        {
            name: "sourcePublishedFrom",
            key: "sourcePublishedFrom",
            label: "Published from",
            render: (props) => <TimestampItem {...props} />
        },
        {
            name: "sourcePublishedTo",
            key: "sourcePublishedTo",
            label: "Published to",
            render: (props) => <TimestampItem {...props} />
        },
        {
            name: "lastModifiedFrom",
            key: "lastModifiedFrom",
            label: "Last modified from",
            render: (props) => <TimestampItem {...props} />
        },
        {
            name: "lastModifiedTo",
            key: "lastModifiedTo",
            label: "Last modified to",
            render: (props) => <TimestampItem {...props} />
        },
        {
            name: "dataChangedFrom",
            key: "dataChangedFrom",
            label: "Data changed from",
            render: (props) => <TimestampItem {...props} />
        },
        {
            name: "dataChangedTo",
            key: "dataChangedTo",
            label: "Data changed to",
            render: (props) => <TimestampItem {...props} />
        },
        {
            name: "attributesChangedFrom",
            key: "attributesChangedFrom",
            label: "Attributes changed from",
            render: (props) => <TimestampItem {...props} />
        },
        {
            name: "attributesChangedTo",
            key: "attributesChangedTo",
            label: "Attributes changed to",
            render: (props) => <TimestampItem {...props} />
        }
    );

    if (showAiAttributeFilters) {
        items.push({
            name: "aiAttributeFilter",
            key: "aiAttributeFilter",
            label: "AI attribute",
            multiple: true,
            initialValue: () => ({aiAttributeId: undefined, operator: undefined, value: undefined}),
            render: (props) => <AiAttributeFilterItem {...props}/>
        });
    }

    return (
        <SearchFilter
            items={items}
            className={props.className}
            values={props.values}
            defaultValues={createDefaultVulSourceItemFilterValues()}
            persistentStateIdent={props.persistentStateIdent}
            defaultActiveFilterNames={["createdFrom", "sources", "vulView", "cveId"]}
            immediateMode={props.immediateMode}
            onChange={props.onChange}
        />
    )
}

export default VulSourceItemFilter;
